import React, { useEffect, useMemo, useState } from "react"
import { CreditCard, Download, FileText, CheckCircle } from "lucide-react"
import DrawerWrapper from "./DrawerWrapper"
import FileImport from "./shared/FileList"
import http from "../config/http"

export default function PaymentDrawer({
  open,
  onClose,
  editingPayment,
  customersList,
  onSaved, // (savedPayment, mode) => void  mode: 'create' | 'update'
  preSelectedInvoice, // Optional: Invoice object from InvoiceDetail page
  preSelectedCustomer, // Optional: Customer ID from InvoiceDetail page
}) {
  const [customerInvoices, setCustomerInvoices] = useState([])
  const [selectedInvoice, setSelectedInvoice] = useState(null)

  const [formData, setFormData] = useState({
    serie: "",
    paymentNumber: "",
    customerId: "",
    invoiceId: "",
    paymentDate: new Date().toISOString().split("T")[0],
    amountToPay: "",
    type: "Payment",
    paymentMethod: "",
    reference: "",
    notes: "",
    balanceBefore: null,
    balanceAfter: null,
    _id: undefined,
    related: undefined,
    uuid: null,
    statusCFDI: null,
    linkxml: null,
    linkpdf: null,
  })

  // Fetch invoices for a customer
  const handleCustomerChange = async (customerId, keepInvoiceSelection = false) => {
    if (!keepInvoiceSelection) {
      setFormData((prev) => ({
        ...prev,
        customerId,
        invoiceId: "",
        amountToPay: "",
      }))
      setSelectedInvoice(null)
    } else {
      setFormData((prev) => ({ ...prev, customerId }))
    }

    setCustomerInvoices([])

    if (!customerId) return

    try {
      const { data } = await http.get(`/api/invoices?customerId=${customerId}`)
      const invoices = keepInvoiceSelection
        ? data
        : data.filter((inv) => inv.status === "pending" || inv.status === "partial")
      
      console.log("Fetched invoices for customer:", invoices)
      console.log("keepInvoiceSelection:", keepInvoiceSelection)
      console.log("Current formData.invoiceId:", formData.invoiceId)
      setCustomerInvoices(invoices)
    } catch (err) {
      console.error("Error fetching customer invoices:", err)
      setCustomerInvoices([])
    }
  }

  const handleInvoiceChange = (invoiceId) => {
    const invoice = customerInvoices.find((inv) => inv._id === invoiceId)
    setSelectedInvoice(invoice || null)
    setFormData((prev) => ({
      ...prev,
      invoiceId,
      amountToPay: invoice && invoice.remainingBalance != null ? invoice.remainingBalance.toString() : "",
    }))
  }

  const getBalanceBefore = () => {
    if (editingPayment && formData.balanceBefore !== null) return formData.balanceBefore
    return selectedInvoice?.remainingBalance || 0
  }

  const getBalanceAfter = () => {
    if (editingPayment && formData.balanceAfter !== null) return formData.balanceAfter
    const before = getBalanceBefore()
    const paid = parseFloat(formData.amountToPay) || 0
    return Math.max(0, before - paid)
  }

  const resetState = () => {
    setCustomerInvoices([])
    setSelectedInvoice(null)
    setFormData({
      serie: "",
      paymentNumber: "",
      customerId: "",
      invoiceId: "",
      paymentDate: new Date().toISOString().split("T")[0],
      amountToPay: "",
      type: "Payment",
      paymentMethod: "",
      reference: "",
      notes: "",
      balanceBefore: null,
      balanceAfter: null,
      _id: undefined,
      related: undefined,
    })
  }

  // When drawer opens, initialize form (new vs edit)
  useEffect(() => {
    if (!open) return

    const init = async () => {
      if (editingPayment) {
        console.log('Loading existing payment:', editingPayment)
        
        const paymentDate = editingPayment.paymentDate || editingPayment.date
          ? (editingPayment.paymentDate || editingPayment.date).split("T")[0]
          : new Date().toISOString().split("T")[0]

        const customerId = editingPayment.invoiceId?.customerId?._id || editingPayment.customerId
        const invoiceId = editingPayment.invoiceId?._id

        console.log('Extracted IDs:', { customerId, invoiceId })

        // Pre-select invoice (even before invoices list loads)
        if (editingPayment.invoiceId) {
          setSelectedInvoice({
            remainingBalance: editingPayment.invoiceId?.remainingBalance ?? 0,
            ...editingPayment.invoiceId,
          })
        }

        if (customerId) {
          await handleCustomerChange(customerId, true)
        }

        const newFormData = {
          serie: editingPayment.serie || "",
          paymentNumber: editingPayment.paymentNumber || "",
          customerId: customerId || "",
          invoiceId: invoiceId || "",
          paymentDate,
          amountToPay: editingPayment.amount?.toString() || "",
          type: editingPayment.type || "Payment",
          paymentMethod: editingPayment.method || "",
          reference: editingPayment.reference || "",
          notes: editingPayment.notes || "",
          balanceBefore: editingPayment.balanceBefore ?? null,
          balanceAfter: editingPayment.balanceAfter ?? null,
          _id: editingPayment._id,
          related: editingPayment.related,
          uuid: editingPayment.uuid || null,
          statusCFDI: editingPayment.statusCFDI || null,
          linkxml: editingPayment.linkxml || null,
          linkpdf: editingPayment.linkpdf || null,
        }
        
        console.log('Setting formData with invoiceId:', newFormData.invoiceId)
        setFormData(newFormData)
      } else {
        // New payment: generate number
        resetState()
        
        // Handle pre-selected invoice and customer from InvoiceDetail
        if (preSelectedInvoice && preSelectedCustomer) {
          setSelectedInvoice(preSelectedInvoice)
          await handleCustomerChange(preSelectedCustomer, true)
          
          setFormData((prev) => ({
            ...prev,
            customerId: preSelectedCustomer,
            invoiceId: preSelectedInvoice._id,
            amountToPay: preSelectedInvoice.remainingBalance?.toString() || "",
          }))
        }
        
        try {
          const { data } = await http.post("/api/payments/generate-number")
          setFormData((prev) => ({
            ...prev,
            serie: data.serie || "",
            paymentNumber: data.paymentNumber || "",
          }))
        } catch (err) {
          console.error("Error generating payment number:", err)
        }
      }
    }

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingPayment?._id, preSelectedInvoice?._id, preSelectedCustomer])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleTimbrar = async () => {
    if (!formData._id) {
      alert('Please save the payment first')
      return
    }

    console.log('handleTimbrar called')
    console.log('Current formData.invoiceId:', formData.invoiceId)
    console.log('Current formData:', formData)
    console.log('customerInvoices available:', customerInvoices.map(inv => ({ _id: inv._id, invoiceNumber: inv.invoiceNumber })))

    try {
      const payload = {
        serie: formData.serie,
        paymentNumber: formData.paymentNumber,
        customerId: formData.customerId,
        invoiceId: formData.invoiceId,
        date: formData.paymentDate,
        amount: parseFloat(formData.amountToPay),
        type: formData.type,
        method: formData.paymentMethod,
        reference: formData.reference,
        notes: formData.notes,
        balanceBefore: getBalanceBefore(),
        balanceAfter: getBalanceAfter(),
      }

      console.log('Sending timbrar request with:', { id: formData._id, payload })
      console.log('Payload invoiceId:', payload.invoiceId)

      const response = await http.post(`/api/payments/timbrar/${formData._id}`, payload)
      console.log('Timbrar response:', response.data)
      // Check if the response indicates success or failure
      if (response.data.success === false) {
        console.error('Timbrado failed:', response.data)
        alert('Error al timbrar el pago: ' + (response.data.error || 'Error desconocido'))
        return
      }

      console.log('Timbrado exitoso:', response.data)
      alert('Pago timbrado exitosamente')

      // Update form data with timbrado information
      setFormData((prev) => ({
        ...prev,
        uuid: response.data.uuid,
        statusCFDI: response.data.statusCFDI || 'Timbrado',
        linkxml: response.data.linkxml,
        linkpdf: response.data.linkpdf,
      }))
    } catch (error) {
      console.error('Error al timbrar:', error)
      alert('Error al timbrar el pago: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const payload = {
      serie: formData.serie,
      paymentNumber: formData.paymentNumber,
      customerId: formData.customerId,
      invoiceId: formData.invoiceId,
      date: formData.paymentDate,
      amount: parseFloat(formData.amountToPay),
      type: formData.type,
      method: formData.paymentMethod,
      reference: formData.reference,
      notes: formData.notes,
      balanceBefore: getBalanceBefore(),
      balanceAfter: getBalanceAfter(),
    }

    try {
      if (editingPayment) {
        await http.put(`/api/payments/${editingPayment._id}`, payload)
        onSaved?.({ ...editingPayment, ...payload }, "update")
      } else {
        const { data: created } = await http.post("/api/payments", payload)
        onSaved?.(created, "create")
      }
      onClose?.()
      resetState()
    } catch (err) {
      console.error("Error saving payment:", err)
    }
  }

  const title = useMemo(() => (editingPayment ? "Edit Payment" : "New Payment"), [editingPayment])

  return (
    <DrawerWrapper open={open} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Payment Number Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
            Payment Number
          </h3>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Serie and payment number
            </label>
            <input
              type="text"
              value={[formData.serie, formData.paymentNumber].filter(Boolean).join('-')}
              readOnly
              aria-label="Serie and payment number"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 font-mono text-sm font-semibold tracking-wide text-gray-800"
              placeholder="P-001"
            />
            <p className="mt-1.5 text-xs text-gray-400">Generated automatically from the configured payment series.</p>
          </div>
        </div>

        {/* Customer and Invoice Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
            Customer & Invoice
          </h3>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Customer
            </label>
            <select
              name="customerId"
              value={formData.customerId}
              onChange={(e) => handleCustomerChange(e.target.value)}
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Select Customer</option>
              {customersList.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Invoice
            </label>
            <select
              name="invoiceId"
              value={formData.invoiceId}
              onChange={(e) => handleInvoiceChange(e.target.value)}
              required
              disabled={!formData.customerId}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select Invoice</option>
              {customerInvoices.map((inv) => (
                <option key={inv._id} value={inv._id}>
                  {inv.invoiceNumber} - Balance: ${inv.remainingBalance}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Payment Details Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
            Payment Details
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Payment Date
              </label>
              <input
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Payment Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="Payment">Payment</option>
                <option value="Partial">Partial</option>
                <option value="Deposit">Deposit</option>
                <option value="Refund">Refund</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Payment Method
            </label>
            <select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Select Method</option>
              <option value="Cash">Cash</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Check">Check</option>
              <option value="PayPal">PayPal</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Reference Number
            </label>
            <input
              type="text"
              name="reference"
              value={formData.reference}
              onChange={handleInputChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Transaction reference or check number"
            />
          </div>
        </div>

        {/* Amount Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
            Amount Information
          </h3>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Balance Before
              </label>
              <div className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-700 font-medium">
                ${getBalanceBefore().toFixed(2)}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Monto Pagado
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                  $
                </span>
                <input
                  type="number"
                  name="amountToPay"
                  value={formData.amountToPay}
                  onChange={handleInputChange}
                  required
                  min="0"
                  max={getBalanceBefore()}
                  step="0.01"
                  className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Balance After
              </label>
              <div className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-green-50 text-green-700 font-medium">
                ${getBalanceAfter().toFixed(2)}
              </div>
            </div>
          </div>

          {editingPayment && formData.balanceBefore !== null && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
              <p className="text-blue-800 font-medium mb-1">Payment Record</p>
              <p className="text-blue-700">
                This payment reduced the balance from ${formData.balanceBefore.toFixed(2)} to $
                {formData.balanceAfter.toFixed(2)}
              </p>
            </div>
          )}
        </div>

        {/* CFDI Information Card - Only show if uuid exists */}
        {formData.uuid && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-medium text-gray-900 flex gap-2 items-center">
                  <CheckCircle className={`h-4 w-4 ${formData.statusCFDI === 'Cancelado' ? 'text-red-600' : 'text-green-600'}`} />
                  CFDI
                </h3>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    UUID
                  </label>
                  <p className="text-xs text-gray-800 font-mono break-all">
                    {formData.uuid}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Status
                  </label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                    formData.statusCFDI === 'Cancelado' 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {formData.statusCFDI || 'Timbrado'}
                  </span>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (formData.linkxml) {
                        window.open(formData.linkxml, '_blank')
                      } else {
                        alert('XML link not available')
                      }
                    }}
                    disabled={!formData.linkxml}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-3 w-3" />
                    XML
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      if (formData.linkpdf) {
                        window.open(formData.linkpdf, '_blank')
                      } else {
                        alert('PDF link not available')
                      }
                    }}
                    disabled={!formData.linkpdf}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="h-3 w-3" />
                    PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
            Notes
          </h3>

          <div>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              className="w-full border border-gray-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Payment notes or additional details..."
            />
          </div>
        </div>

        <FileImport module="payments" parentId={formData._id} files={formData?.related?.attachments} />

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => {
              onClose?.()
              resetState()
            }}
            className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 py-2.5 rounded-lg font-medium transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleTimbrar}
            disabled={!formData._id || formData.uuid}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            title={formData.uuid ? 'Pago ya timbrado' : (!formData._id ? 'Guarde el pago primero' : 'Timbrar Pago')}
          >
            <CreditCard className="h-4 w-4" />
            Timbrar
          </button>
          <button
            type="submit"
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm"
          >
            {editingPayment ? "Update Payment" : "Record Payment"}
          </button>
        </div>
      </form>
    </DrawerWrapper>
  )
}
